import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { CLPrint, Structures } from '@clavisco/core';
import {
  ICLCallbacksInterface,
  CL_CHANNEL,
  LinkerService,
  Register,
  Run,
  StepDown,
  ICLEvent,
} from '@clavisco/linker';
import {
  IFileInfo,
  IBrowserDownloadFile,
  IDirectoryItem,
  ILogsFilesRequestInformation,
} from '@clavisco/logs-viewer';
import { OverlayService } from '@clavisco/overlay';
import { finalize, Subscription } from 'rxjs';
import ICLResponse = Structures.Interfaces.ICLResponse;
import { PhysicalLogsService } from 'src/app/core/services/physical-logs.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-viewer',
  templateUrl: './viewer.component.html',
  styleUrls: ['./viewer.component.scss'],
})
export class ViewerComponent implements OnInit, AfterViewInit {
  /**
   * Stores the height of the page container, dynamically calculated based on the toolbar height.
   */
  pageContainerHeight: string = '';

  /**
   * The componentId is used to uniquely identify this component in the application.
   * It is used for logging, debugging, and event handling.
   */
  componentId: string = '';

  /**
   * Manages all subscriptions made in the component to ensure proper cleanup on destruction.
   */
  subscriptions$!: Subscription;

  /**
   * Stores the callbacks for the component, used to handle events and actions.
   */
  callbacks: ICLCallbacksInterface<CL_CHANNEL> = {
    Callbacks: {},
    Tracks: [],
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject('LinkerService') private linkerService: LinkerService,
    private logsService: PhysicalLogsService,
    private overlayService: OverlayService
  ) {
    // Initialize the component by setting up necessary configurations.
    this.onLoad();
  }

  /**
   * Lifecycle hook that is called once the component is initialized.
   * This method is currently empty and can be used for initialization logic.
   */
  ngOnInit(): void {
    // TODO: Add initialization logic here if needed.
  }

  /**
   * Lifecycle hook that is called after the component's view has been fully initialized.
   * Publishes a pre-request records event to the linker service and calculates dynamic heights.
   */
  ngAfterViewInit(): void {
    this.CalculateHeights();

    this.linkerService.Publish({
      Target: this.componentId,
      CallBack: CL_CHANNEL.PRE_REQ_RECORDS,
      Data: '',
    });
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * Ensures that all active subscriptions are unsubscribed to prevent memory leaks.
   */
  ngOnDestroy(): void {
    if (this.subscriptions$) {
      this.subscriptions$.unsubscribe();
    }
  }

  /**
   * Initializes the component by setting up subscriptions, registering callbacks,
   * and preparing the linker service flow for handling events and actions.
   * This method is called during the component's construction phase.
   */
  private onLoad(): void {
    // Initialize the subscriptions object to manage all subscriptions in the component.
    this.subscriptions$ = new Subscription();

    // Set the component ID for identifying this component in the application.
    this.componentId = 'LogsViewerComponent';

    // Register a callback to handle requests for log directory items.
    Register(
      this.componentId,
      CL_CHANNEL.REQUEST_RECORDS,
      this.GetLogsDirectoryItemsEventHandler,
      this.callbacks
    );

    // Register a callback to handle input events for downloading log directory files.
    Register(
      this.componentId,
      CL_CHANNEL.INPUT,
      this.DownloadLogsDirectoryFile,
      this.callbacks
    );

    // Subscribe to the linker service's flow and process callbacks using the provided pipeline.
    this.subscriptions$.add(
      this.linkerService
        .Flow()
        ?.pipe(StepDown(this.callbacks))
        .subscribe({
          // Handle successful callbacks by running the associated logic.
          next: (callback) =>
            Run(callback.Target, callback, this.callbacks.Callbacks),
          // Log any errors that occur during the subscription.
          error: (error) => CLPrint(error),
        })
    );
  }

  /**
   * Dynamically calculates the height of the page container based on the toolbar height.
   * This ensures that the layout adapts to the available viewport space.
   */
  private CalculateHeights(): void {
    setTimeout((): void => {
      let toolbar: Element = this.document.querySelector('.header-container')!;
      let toolbarHeight: number = +window
        .getComputedStyle(toolbar)
        .height.replace('px', '');
      this.pageContainerHeight = `calc(100vh - ${toolbarHeight}px)`;
    }, 0);
  }

  /**
   * Handles the download of a log directory file.
   * This method is triggered by an event and uses the logsService to download the file.
   * @param _clEvent - The event containing the file information to be downloaded.
   */
  private DownloadLogsDirectoryFile = (_clEvent: ICLEvent): void => {
    // Parse the event data to extract file information.
    let logFileInfo: IFileInfo = JSON.parse(_clEvent.Data) as IFileInfo;

    // Display an overlay to indicate a loading state.
    this.overlayService.OnGet();

    // Call the logsService to download the file and handle the response.
    this.logsService
      .DownloadLogsDirectoryItem(logFileInfo.FullName)
      .pipe(
        // Ensure the overlay is removed after the operation completes.
        finalize(() => this.overlayService.Drop())
      )
      .subscribe({
        // Publish the downloaded file data to the linker service.
        next: (response: ICLResponse<IBrowserDownloadFile>) => {
          this.linkerService.Publish({
            CallBack: CL_CHANNEL.DATA_LINE_1,
            Data: JSON.stringify(response.Data),
            Target: this.componentId,
          });
        },
      });
  };

  /**
   * Retrieves log directory items based on the provided date range and directory path.
   * This method uses the logsService to fetch the items and publishes the results.
   * @param pStartDate - The start date for filtering log items.
   * @param pEndDate - The end date for filtering log items.
   * @param pLogsDirectory - (Optional) The directory path to fetch logs from.
   */
  private GetLogsDirectoryItems(
    pStartDate: Date,
    pEndDate: Date,
    pLogsDirectory?: string
  ): void {
    // Display an overlay to indicate a loading state.
    this.overlayService.OnGet();

    // Call the logsService to fetch log directory items and handle the response.
    this.logsService
      .GetLogsDirectoryItems(pStartDate, pEndDate, pLogsDirectory)
      .pipe(
        // Ensure the overlay is removed after the operation completes.
        finalize(() => this.overlayService.Drop())
      )
      .subscribe({
        // Publish the fetched log directory items to the linker service.
        next: (response: ICLResponse<IDirectoryItem[]>) => {
          this.linkerService.Publish({
            CallBack: CL_CHANNEL.INFLATE,
            Data: JSON.stringify(response.Data),
            Target: this.componentId,
          });
        },
      });
  }

  /**
   * Event handler for retrieving log directory items.
   * This method is triggered by an event and calls GetLogsDirectoryItems with the event data.
   * @param _clEvent - The event containing the request information for log directory items.
   */
  private GetLogsDirectoryItemsEventHandler = (_clEvent: ICLEvent): void => {
    // Parse the event data to extract log file request information.
    let logsFileRequestInformation: ILogsFilesRequestInformation = JSON.parse(
      _clEvent.Data
    ) as ILogsFilesRequestInformation;

    // Call GetLogsDirectoryItems with the extracted information.
    this.GetLogsDirectoryItems(
      logsFileRequestInformation.StartDate,
      logsFileRequestInformation.EndDate,
      logsFileRequestInformation.DirectoryPath
    );
  };
}
